/**
 * HouseholdProfilePage — one-stop editor for the household facts that
 * drive every planner on the platform: ages, incomes (base + bonus),
 * target drawdown, retirement age, savings rate, PCLS taken, DB pensions.
 *
 * Writes back to /api/profile (PUT) — any page that consumes the profile
 * (Planning, Pension Optimiser, Drawdown, Scenarios, Jurisdictions,
 * Scenario Stacker) picks up the new values on next fetch.
 */
import { useEffect, useState } from 'react'
import { UserCog, Save, RefreshCw, CheckCircle2 } from 'lucide-react'
import { clsx } from 'clsx'
import { api } from '@/lib/api'
import { useAuth } from '@/hooks/useAuth'

interface Member {
  name: string
  age: number
  date_of_birth?: string
  annual_income_gbp: number
  base_salary_gbp?: number
  bonus_gbp?: number
  tax_band?: string
  ni_record_full_years?: number
  is_director?: boolean
  notes?: string
  tax_band_computed?: string
  marginal_rate_pct?: number
  income_tax_due_gbp?: number
  in_pa_taper_trap?: boolean
}

interface Household {
  marital_status?: string
  children?: number
  estimated_annual_spend_gbp?: number
  target_drawdown_gbp?: number
  target_is_net?: boolean
  annual_savings_gbp?: number
  current_age?: number
  retirement_age?: number
  pcls_taken_gbp?: number
  address?: string
  country?: string
  tax_jurisdiction?: string
}

interface ProfileResponse {
  members: Member[]
  household: Household
  tax_year: string
}

const fmt = (n: number) =>
  `£${n.toLocaleString('en-GB', { maximumFractionDigits: 0 })}`

export default function HouseholdProfilePage() {
  const canConfigure = useAuth(s => s.user?.permissions?.can_configure ?? true)
  const [profile, setProfile] = useState<ProfileResponse | null>(null)
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [saved, setSaved] = useState(false)
  const [error, setError] = useState<string | null>(null)

  // Local edit state — initialised on fetch
  const [members, setMembers] = useState<Member[]>([])
  const [household, setHousehold] = useState<Household>({})

  const fetchProfile = async () => {
    setLoading(true)
    setError(null)
    try {
      const res = await api.get<ProfileResponse>('/api/profile')
      setProfile(res.data)
      setMembers(res.data.members)
      setHousehold(res.data.household)
    } catch (err: any) {
      setError(err?.message ?? 'Failed to load profile')
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    fetchProfile()
  }, [])

  const save = async () => {
    setSaving(true)
    setError(null)
    try {
      await api.put('/api/profile', {
        members: members.map(m => ({
          name: m.name,
          age: m.age,
          date_of_birth: m.date_of_birth,
          base_salary_gbp: m.base_salary_gbp,
          bonus_gbp: m.bonus_gbp,
          annual_income_gbp:
            (m.base_salary_gbp ?? 0) + (m.bonus_gbp ?? 0) || m.annual_income_gbp,
          ni_record_full_years: m.ni_record_full_years,
          is_director: m.is_director,
          notes: m.notes,
        })),
        household: {
          marital_status: household.marital_status,
          children: household.children,
          estimated_annual_spend_gbp: household.estimated_annual_spend_gbp,
          target_drawdown_gbp: household.target_drawdown_gbp,
          target_is_net: household.target_is_net,
          annual_savings_gbp: household.annual_savings_gbp,
          current_age: household.current_age,
          retirement_age: household.retirement_age,
          pcls_taken_gbp: household.pcls_taken_gbp,
          tax_jurisdiction: household.tax_jurisdiction,
        },
      })
      await fetchProfile()
      setSaved(true)
      setTimeout(() => setSaved(false), 3000)
    } catch (err: any) {
      setError(err?.message ?? 'Save failed')
    } finally {
      setSaving(false)
    }
  }

  const updateMember = (idx: number, patch: Partial<Member>) => {
    setMembers(prev => prev.map((m, i) => (i === idx ? { ...m, ...patch } : m)))
  }

  const updateHousehold = (patch: Partial<Household>) => {
    setHousehold(prev => ({ ...prev, ...patch }))
  }

  if (loading && !profile) {
    return (
      <div className="space-y-4 animate-fade-in">
        <div className="skeleton h-8 w-64" />
        <div className="skeleton h-64 rounded-xl" />
      </div>
    )
  }

  if (error && !profile) {
    return (
      <div className="card p-6 text-center">
        <p className="text-rose-light">{error}</p>
        <button type="button" onClick={fetchProfile} className="btn-secondary mt-3">Retry</button>
      </div>
    )
  }

  return (
    <div className="space-y-6 animate-fade-in">
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="page-title flex items-center gap-2">
            <UserCog size={20} className="text-blue-light" aria-hidden />
            Household Profile
          </h1>
          <p className="text-sm text-text-secondary">
            The facts that drive every planner. Change anything here and it flows to the FIRE,
            Pension, Drawdown, and Scenario pages on next refresh.
          </p>
          {profile && (
            <p className="text-xs text-text-muted mt-1">
              UK Tax Year: {profile.tax_year}
            </p>
          )}
        </div>
        {canConfigure && (
          <div className="flex items-center gap-2">
            {saved && (
              <span className="text-xs text-emerald flex items-center gap-1">
                <CheckCircle2 size={13} aria-hidden /> Saved
              </span>
            )}
            <button
              type="button"
              onClick={fetchProfile}
              className="btn-secondary gap-2 text-xs"
              disabled={saving}
            >
              <RefreshCw size={13} aria-hidden />
              Revert
            </button>
            <button
              type="button"
              onClick={save}
              disabled={saving}
              className="btn-primary gap-2 text-xs"
            >
              <Save size={13} aria-hidden />
              {saving ? 'Saving...' : 'Save changes'}
            </button>
          </div>
        )}
      </div>

      {!canConfigure && (
        <div className="card p-3 border-blue/40 bg-blue/5 text-sm text-text-primary">
          <span className="font-semibold text-blue-light">Demo view.</span>{' '}
          You are logged in as a viewer — the household profile is read-only.
        </div>
      )}

      {error && (
        <div className="card p-4 border-rose/30 bg-rose/5 text-sm text-rose-light">
          {error}
        </div>
      )}

      {/* Everything below here is input state — fieldset disables it for
          read-only viewers so the form can't accept doomed edits. */}
      <fieldset disabled={!canConfigure} className={clsx(!canConfigure && 'opacity-70 cursor-not-allowed')}>
      {/* Members table */}
      <section className="card p-5">
        <h2 className="panel-title mb-4">People</h2>
        <div className="overflow-x-auto">
          <table className="table-pro w-full text-xs">
            <thead>
              <tr>
                <th>Name</th>
                <th>Age</th>
                <th>DOB</th>
                <th className="text-right">Base £/yr</th>
                <th className="text-right">Bonus £/yr</th>
                <th className="text-right">Gross</th>
                <th className="text-right">Band</th>
                <th className="text-right">Marginal</th>
                <th className="text-right">Tax due</th>
              </tr>
            </thead>
            <tbody>
              {members.map((m, idx) => {
                const gross = (m.base_salary_gbp ?? 0) + (m.bonus_gbp ?? 0)
                return (
                  <tr key={m.name}>
                    <td className="font-semibold text-text-primary">{m.name}</td>
                    <td>
                      <input
                        type="number"
                        value={m.age}
                        onChange={(e) => updateMember(idx, { age: parseInt(e.target.value || '0') })}
                        className="input w-14 px-2 py-1"
                      />
                    </td>
                    <td>
                      <input
                        type="date"
                        value={m.date_of_birth ?? ''}
                        onChange={(e) => updateMember(idx, { date_of_birth: e.target.value })}
                        className="input w-auto px-2 py-1 text-[11px]"
                      />
                    </td>
                    <td className="text-right">
                      <input
                        type="number"
                        value={m.base_salary_gbp ?? 0}
                        step={1000}
                        onChange={(e) => updateMember(idx, { base_salary_gbp: parseFloat(e.target.value || '0') })}
                        className="input w-24 px-2 py-1 text-right font-mono"
                      />
                    </td>
                    <td className="text-right">
                      <input
                        type="number"
                        value={m.bonus_gbp ?? 0}
                        step={500}
                        onChange={(e) => updateMember(idx, { bonus_gbp: parseFloat(e.target.value || '0') })}
                        className="input w-24 px-2 py-1 text-right font-mono"
                      />
                    </td>
                    <td className="text-right font-mono font-bold text-text-primary">
                      {fmt(gross)}
                    </td>
                    <td className="text-right">
                      <span className={clsx(
                        'badge text-[10px]',
                        m.tax_band_computed === 'additional' && 'badge-red',
                        m.tax_band_computed === 'higher' && 'badge-amber',
                        m.tax_band_computed === 'basic' && 'badge-blue',
                        m.tax_band_computed === 'below_personal_allowance' && 'badge-green',
                      )}>
                        {m.tax_band_computed ?? '—'}
                      </span>
                    </td>
                    <td className="text-right font-mono text-text-primary">
                      {m.marginal_rate_pct?.toFixed(0)}%
                      {m.in_pa_taper_trap && (
                        <span className="ml-1 text-[9px] text-rose-light" title="60% effective rate in PA taper trap">*</span>
                      )}
                    </td>
                    <td className="text-right font-mono text-text-muted">
                      {fmt(m.income_tax_due_gbp ?? 0)}
                    </td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        </div>
        <p className="text-[10px] text-text-muted mt-3">
          * = in the £100k–£125,140 personal allowance taper trap (60% effective marginal rate).
          Computed values (band, marginal rate, tax due) refresh after Save.
        </p>
      </section>

      {/* Retirement targets */}
      <section className="card p-5">
        <h2 className="panel-title mb-4">Retirement Targets</h2>
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
          <LabeledNumber
            label="Target drawdown (gross £/yr)"
            value={household.target_drawdown_gbp ?? 220_000}
            onChange={(v) => updateHousehold({ target_drawdown_gbp: v })}
            step={5000}
          />
          <LabeledNumber
            label="Estimated spend £/yr"
            value={household.estimated_annual_spend_gbp ?? 220_000}
            onChange={(v) => updateHousehold({ estimated_annual_spend_gbp: v })}
            step={5000}
          />
          <LabeledNumber
            label="Annual savings (both ISAs) £/yr"
            value={household.annual_savings_gbp ?? 40_000}
            onChange={(v) => updateHousehold({ annual_savings_gbp: v })}
            step={1000}
          />
          <LabeledNumber
            label="PCLS already taken £"
            value={household.pcls_taken_gbp ?? 325_000}
            onChange={(v) => updateHousehold({ pcls_taken_gbp: v })}
            step={1000}
          />
          <LabeledNumber
            label="Simon's current age"
            value={household.current_age ?? 57}
            onChange={(v) => updateHousehold({ current_age: v })}
            step={1}
          />
          <LabeledNumber
            label="Target retirement age"
            value={household.retirement_age ?? 60}
            onChange={(v) => updateHousehold({ retirement_age: v })}
            step={1}
          />
          <div>
            <label className="text-[10px] uppercase tracking-wider text-text-muted block mb-1">
              Target is net (post-tax)?
            </label>
            <label className="flex items-center gap-2 mt-2">
              <input
                type="checkbox"
                checked={household.target_is_net ?? false}
                onChange={(e) => updateHousehold({ target_is_net: e.target.checked })}
                className="rounded border-border bg-surface-card"
              />
              <span className="text-xs text-text-secondary">
                {household.target_is_net ? 'Net (take-home)' : 'Gross (pre-tax)'}
              </span>
            </label>
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-wider text-text-muted block mb-1">
              Tax jurisdiction
            </label>
            <input
              type="text"
              value={household.tax_jurisdiction ?? ''}
              onChange={(e) => updateHousehold({ tax_jurisdiction: e.target.value })}
              className="input px-2 py-1.5 text-xs"
              placeholder="e.g. England & NI"
            />
          </div>
        </div>
      </section>

      {/* Methodology / flow */}
      <details className="card p-4 text-xs text-text-secondary">
        <summary className="cursor-pointer text-text-primary font-semibold">
          What uses these values?
        </summary>
        <ul className="mt-3 space-y-1.5 list-disc list-inside">
          <li><code>base_salary_gbp</code> + <code>bonus_gbp</code> → auto-sums into <code>annual_income_gbp</code></li>
          <li><code>annual_income_gbp</code> → Pension Optimiser tax relief, marginal rate calc, PA taper detection</li>
          <li><code>current_age</code> + <code>retirement_age</code> → FIRE planner horizon, Build-Up chart, Drawdown sequencing</li>
          <li><code>target_drawdown_gbp</code> → Planning page, Drawdown blend, Monte Carlo, Jurisdictions comparison</li>
          <li><code>annual_savings_gbp</code> → Build-Up projection, FIRE years-to-target</li>
          <li><code>pcls_taken_gbp</code> → Drawdown Optimiser (already-crystallised PCLS not available)</li>
        </ul>
        <p className="mt-3 pt-3 border-t border-border text-text-muted">
          Save changes and refresh any planner page to see the numbers propagate. Nothing is
          persisted to disk right now (in-memory only) — edits survive backend restarts but not
          redeploys. A proper database-backed profile is on the backlog.
        </p>
      </details>
      </fieldset>
    </div>
  )
}

function LabeledNumber({
  label,
  value,
  onChange,
  step = 1,
}: {
  label: string
  value: number
  onChange: (v: number) => void
  step?: number
}) {
  return (
    <div>
      <label className="text-[10px] uppercase tracking-wider text-text-muted block mb-1">
        {label}
      </label>
      <input
        type="number"
        value={value}
        step={step}
        onChange={(e) => onChange(parseFloat(e.target.value || '0'))}
        className="input px-2 py-1.5 text-xs font-mono"
      />
    </div>
  )
}
